import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, DatePicker, Empty, Input, Popconfirm, Progress, Select, Switch, Table, Tag } from 'antd';
import {
  deleteOrderGenieRequest,
  getOrderGenieProcessStatusBulk,
  listOrderGenieRequests,
  stopOrderGenieProcess,
} from '../../services/orderXApi';

const { RangePicker } = DatePicker;
const { Search } = Input;

const LIST_PAGE_SIZE = 100;
const REQUEST_NAME_DEBOUNCE_MS = 350;
const CUSTOMER_DEBOUNCE_MS = 350;
const LIST_POLL_MS = 6000;
const PROGRESS_POLL_MS = 4000;

// Statuses the backend treats as finished — nothing further will happen to
// these requests, so there's no running task left to stop.
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);
// Statuses that still have (or are waiting for) an active processing task,
// so "Stop" is meaningful for them. 'parsed'/'matched' sit idle awaiting
// user review rather than running, so Stop doesn't apply there.
const STOPPABLE_STATUSES = new Set(['draft', 'queued', 'uploaded', 'matching']);

// Fixed, known set of status values the backend can write, used to populate the
// status filter dropdown -- this can't be derived from the fetched rows alone
// since a given page of results may not contain every status.
const STATUS_OPTIONS = [
  'draft',
  'queued',
  'uploaded',
  'matching',
  'parsed',
  'matched',
  'completed',
  'failed',
  'cancelled',
].map((status) => ({ label: status, value: status }));

const formatUpdatedAt = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

// Lists the current user's OrderX v4 requests, regardless of status, so they can
// track a request from the moment it's queued through completion, pick one up to
// resume the wizard at Step 1 (Review Matches), stop one that's still running, or
// delete one that's finished. This is an additive discovery path alongside (not a
// replacement for) the step-0 pendingRequest banner.
const MyRequestsList = ({ userId, active, onResume, onRerunMatches }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resumingId, setResumingId] = useState(null);
  const [rerunningId, setRerunningId] = useState(null);
  const [stoppingId, setStoppingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // request id -> { message, stage_done, stage_total } live progress, refreshed
  // on a tighter interval than the row list itself so in-flight requests visibly
  // move without the whole table re-rendering/flashing every tick.
  const [progressById, setProgressById] = useState({});

  // Server-side filters: changing any of these must trigger a refetch.
  const [allUsers, setAllUsers] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]); // [dateFrom, dateTo] ISO date strings
  const [requestNameInput, setRequestNameInput] = useState('');
  const [requestName, setRequestName] = useState(''); // debounced value actually used for fetching

  const [statusFilter, setStatusFilter] = useState([]);
  const [customerFilterInput, setCustomerFilterInput] = useState('');
  const [customerFilter, setCustomerFilter] = useState(''); // debounced value actually used for fetching

  const requestNameDebounceRef = useRef(null);
  const customerDebounceRef = useRef(null);

  // Guards against a stale fetch response (from a userId change, tab leave, or
  // unmount while the request is in flight) calling setState after the fact.
  const requestTokenRef = useRef(0);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => () => {
    if (requestNameDebounceRef.current) {
      clearTimeout(requestNameDebounceRef.current);
    }
    if (customerDebounceRef.current) {
      clearTimeout(customerDebounceRef.current);
    }
  }, []);

  // Keeps the latest fetched rows available to the progress-polling interval
  // below without having to re-create that interval every time rows change.
  const rowsRef = useRef([]);

  // `silent` skips the loading spinner so the background poll ticks (list
  // refresh + stop/delete follow-ups) don't flash the table every few seconds
  // — only the first load / explicit filter changes show it.
  const loadRequests = useCallback((silent = false) => {
    const trimmedUserId = String(userId || '').trim();
    if (!allUsers && !trimmedUserId) {
      setRows([]);
      return;
    }
    const [dateFrom, dateTo] = dateRange;
    const token = requestTokenRef.current + 1;
    requestTokenRef.current = token;
    if (!silent) setLoading(true);
    setError('');
    listOrderGenieRequests(allUsers ? 'all' : trimmedUserId, {
      lite: true,
      pageSize: LIST_PAGE_SIZE,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      requestName: requestName || undefined,
      statuses: statusFilter,
      customer: customerFilter || undefined,
      // No defaultExcludeTerminal: every request, whatever its status, should
      // show up here — queued/running ones so progress is visible immediately,
      // and completed/failed/cancelled ones so they can be deleted.
    })
      .then((resp) => {
        if (!aliveRef.current || requestTokenRef.current !== token) return;
        const items = Array.isArray(resp?.items) ? resp.items : [];
        setRows(items);
        rowsRef.current = items;
        setLoading(false);
      })
      .catch((err) => {
        if (!aliveRef.current || requestTokenRef.current !== token) return;
        if (!silent) setError(err?.message || 'Unable to load your requests.');
        setLoading(false);
      });
  }, [userId, allUsers, dateRange, requestName, statusFilter, customerFilter]);

  useEffect(() => {
    if (active) loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, userId, allUsers, dateRange, requestName, statusFilter, customerFilter]);

  // Background refresh so a request moves through queued -> matching -> matched
  // (and new requests created elsewhere, e.g. from Step 0) without the user
  // having to leave and come back to the "My Requests" tab.
  useEffect(() => {
    if (!active) return undefined;
    const interval = setInterval(() => loadRequests(true), LIST_POLL_MS);
    return () => clearInterval(interval);
  }, [active, loadRequests]);

  // Finer-grained live progress (message/stage) for rows that are still
  // actively processing, pulled from the bulk process-status endpoint rather
  // than re-querying the full list on a tight loop.
  useEffect(() => {
    if (!active) return undefined;
    const fetchProgress = () => {
      const inProgress = rowsRef.current.filter(
        (r) => STOPPABLE_STATUSES.has(String(r?.status || '').toLowerCase())
      );
      if (!inProgress.length) {
        setProgressById({});
        return;
      }
      const requestUsers = {};
      inProgress.forEach((r) => { requestUsers[String(r.id)] = r.user_id; });
      getOrderGenieProcessStatusBulk({
        user_id: allUsers ? 'all' : String(userId || '').trim(),
        request_ids: inProgress.map((r) => r.id),
        request_users: requestUsers,
      })
        .then((resp) => {
          if (!aliveRef.current) return;
          setProgressById(resp?.statuses || {});
        })
        .catch(() => {});
    };
    fetchProgress();
    const interval = setInterval(fetchProgress, PROGRESS_POLL_MS);
    return () => clearInterval(interval);
  }, [active, allUsers, userId, rows]);

  // Pass the row's own owner user_id through, not the viewer's — in "All users"
  // mode a row can belong to someone else (or to the synthetic "email_inbox"
  // user), and the backend's ownership lookup requires the exact owner user_id.
  // The viewer is only able to see the row in the first place because they're
  // allowed to browse all requests on this account, so acting on it as its
  // actual owner is safe and is what makes Resume/Rerun work for other users'
  // (including email-inbox-originated) requests.
  const handleResumeClick = useCallback((record) => {
    const requestId = record?.id;
    if (requestId == null) return;
    // Disable resume/rerun interactions while one is already in flight so a
    // double-click (row + button, or two rows) can't fire two concurrent actions.
    if (resumingId != null || rerunningId != null || stoppingId != null || deletingId != null) return;
    setResumingId(requestId);
    Promise.resolve(onResume?.(requestId, record?.user_id)).finally(() => {
      if (aliveRef.current) setResumingId(null);
    });
  }, [onResume, resumingId, rerunningId, stoppingId, deletingId]);

  const handleRerunMatchesClick = useCallback((record, e) => {
    e?.stopPropagation();
    const requestId = record?.id;
    if (requestId == null) return;
    if (resumingId != null || rerunningId != null || stoppingId != null || deletingId != null) return;
    setRerunningId(requestId);
    Promise.resolve(onRerunMatches?.(requestId, record?.user_id)).finally(() => {
      if (aliveRef.current) setRerunningId(null);
    });
  }, [onRerunMatches, resumingId, rerunningId, stoppingId, deletingId]);

  const handleStopClick = useCallback((record, e) => {
    e?.stopPropagation();
    const requestId = record?.id;
    if (requestId == null || stoppingId != null || deletingId != null) return;
    setStoppingId(requestId);
    stopOrderGenieProcess(requestId, { user_id: record?.user_id })
      .catch((err) => {
        if (aliveRef.current) setError(err?.message || 'Unable to stop this request.');
      })
      .finally(() => {
        if (!aliveRef.current) return;
        setStoppingId(null);
        loadRequests(true);
      });
  }, [stoppingId, deletingId, loadRequests]);

  const handleDeleteClick = useCallback((record) => {
    const requestId = record?.id;
    if (requestId == null || stoppingId != null || deletingId != null) return;
    setDeletingId(requestId);
    deleteOrderGenieRequest(requestId, record?.user_id)
      .catch((err) => {
        if (aliveRef.current) setError(err?.message || 'Unable to delete this request.');
      })
      .finally(() => {
        if (!aliveRef.current) return;
        setDeletingId(null);
        loadRequests(true);
      });
  }, [stoppingId, deletingId, loadRequests]);

  const handleRequestNameChange = useCallback((e) => {
    const value = e.target.value;
    setRequestNameInput(value);
    if (requestNameDebounceRef.current) {
      clearTimeout(requestNameDebounceRef.current);
    }
    // Clearing (via the allowClear icon, which only fires onChange, not onSearch)
    // should feel instant rather than waiting out the debounce.
    if (!value) {
      setRequestName('');
      return;
    }
    requestNameDebounceRef.current = setTimeout(() => {
      setRequestName(value);
    }, REQUEST_NAME_DEBOUNCE_MS);
  }, []);

  const handleDateRangeChange = useCallback((_dates, dateStrings) => {
    const [from, to] = Array.isArray(dateStrings) ? dateStrings : ['', ''];
    setDateRange([from || null, to || null]);
  }, []);

  const handleCustomerFilterChange = useCallback((e) => {
    const value = e.target.value;
    setCustomerFilterInput(value);
    if (customerDebounceRef.current) {
      clearTimeout(customerDebounceRef.current);
    }
    // Clearing (via the allowClear icon, which only fires onChange) should feel
    // instant rather than waiting out the debounce.
    if (!value) {
      setCustomerFilter('');
      return;
    }
    customerDebounceRef.current = setTimeout(() => {
      setCustomerFilter(value);
    }, CUSTOMER_DEBOUNCE_MS);
  }, []);

  const columns = [
    {
      title: 'Key',
      dataIndex: 'request_code',
      key: 'request_code',
      render: (value, r) => value || (r.account_seq != null ? `OX-${r.account_seq}` : `#${r.id}`),
    },
    { title: 'Request', dataIndex: 'request_name', key: 'request_name', render: (v, r) => v || `Request #${r.id}` },
    ...(allUsers ? [{ title: 'Requested by', dataIndex: 'user_id', key: 'user_id' }] : []),
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => {
        const name = String(record?.customer || '').trim();
        const erpId = String(record?.customer_id_erp || '').trim();
        if (!name && !erpId) return '—';
        if (name && erpId) return `${name} (${erpId})`;
        return name || erpId;
      },
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      key: 'summary',
      ellipsis: true,
      render: (value) => value || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const status = String(record?.status || '').toLowerCase();
        if (!STOPPABLE_STATUSES.has(status)) return '—';
        const p = progressById[String(record.id)];
        if (!p) return 'Queued';
        const total = Number(p.stage_total) || 0;
        const done = Number(p.stage_done) || 0;
        if (total > 0) {
          return <Progress percent={Math.round((done / total) * 100)} size="small" />;
        }
        return p.message || 'Processing…';
      },
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (value) => formatUpdatedAt(value),
    },
    {
      title: 'Items',
      dataIndex: 'total_items',
      key: 'total_items',
      render: (value) => (value == null ? '—' : value),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const status = String(record?.status || '').toLowerCase();
        const busy = resumingId != null || rerunningId != null || stoppingId != null || deletingId != null;
        const canStop = STOPPABLE_STATUSES.has(status);
        const canDelete = TERMINAL_STATUSES.has(status);
        return (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              size="small"
              type="primary"
              loading={resumingId === record.id}
              disabled={busy && resumingId !== record.id}
              onClick={(e) => {
                e.stopPropagation();
                handleResumeClick(record);
              }}
            >
              Resume
            </Button>
            <Button
              size="small"
              loading={rerunningId === record.id}
              disabled={busy && rerunningId !== record.id}
              title="Re-run product matching for this request's already-extracted items, without re-parsing the source file."
              onClick={(e) => handleRerunMatchesClick(record, e)}
            >
              Rerun Matches
            </Button>
            {canStop && (
              <Button
                size="small"
                danger
                loading={stoppingId === record.id}
                disabled={busy && stoppingId !== record.id}
                onClick={(e) => handleStopClick(record, e)}
              >
                Stop
              </Button>
            )}
            {canDelete && (
              <Popconfirm
                title="Delete this request?"
                description="This permanently removes the request and its line items."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={(e) => {
                  e?.stopPropagation?.();
                  handleDeleteClick(record);
                }}
                onCancel={(e) => e?.stopPropagation?.()}
              >
                <Button
                  size="small"
                  danger
                  loading={deletingId === record.id}
                  disabled={busy && deletingId !== record.id}
                  onClick={(e) => e.stopPropagation()}
                >
                  Delete
                </Button>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError('')}
          className="mb-3"
        />
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Switch checked={allUsers} onChange={setAllUsers} />
          <span>All users</span>
        </div>
        <RangePicker
          allowClear
          format="YYYY-MM-DD"
          placeholder={['Updated from', 'Updated to']}
          onChange={handleDateRangeChange}
        />
        <Search
          allowClear
          placeholder="Search by request name"
          value={requestNameInput}
          onChange={handleRequestNameChange}
          onSearch={(value) => setRequestName(value)}
          style={{ width: 220 }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter by status (default: all)"
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ minWidth: 260 }}
        />
        <Input
          allowClear
          placeholder="Filter by customer"
          value={customerFilterInput}
          onChange={handleCustomerFilterChange}
          style={{ width: 200 }}
        />
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: (
            <Empty
              description="No requests found. New requests show up here as soon as they're queued."
            />
          ),
        }}
        onRow={(record) => ({
          onClick: () => handleResumeClick(record),
          style: {
            cursor: [resumingId, rerunningId, stoppingId, deletingId].every((v) => v == null)
              ? 'pointer'
              : 'default',
          },
        })}
      />
    </div>
  );
};

export default MyRequestsList;
